import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Options as HighchartOptions } from 'highcharts';
import { Subscription } from 'rxjs';
import uuid from 'uuid-random';

import { getShowRecessions } from 'TargetRatesApp/apps/target-rates-app/state/target-rates.selectors';

import {
    HIGHCHARTS_YIELD_CURVE_DEFAULT_OPTIONS,
    MAX_DATES,
    YIELD_CURVE_EXPORT_OPTIONS,
    YIELD_CURVE_TERMS,
    YIELD_CURVE_UNITS,
} from './constants';
import { STATIC_PDF_FEATURE } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.constants';
import { ExportOption } from 'ChartApp/ts/services/export-service/enum';
import { YieldCurveChartPoint, YieldCurveDateSeries } from './interfaces';
import { SimpleDropdownConfig } from 'Angular/features/components/dropdowns/simple-dropdown/interfaces/simple-dropdown-config.interface';
import { TargetRatesRootStore } from 'TargetRatesApp/interfaces/state.interface';

import { ExportService } from 'ChartApp/ts/services/export-service/export.service';
import { FeatureService } from 'EntitlementsApp/ts/features/feature-needed/feature.service';
import { TargetRatesService } from 'TargetRatesApp/services/target-rates.service';
import { TargetRatesAppStateService } from 'TargetRatesApp/services/target-rates-app-state.service';

import { FeatureNeededComponent } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.component';
import { downloadUrlWrapper } from 'Angular/utils/download';

import Highcharts from './highcharts/highcharts';

@Component({ standalone: false, selector: 'ycn-yield-curve', templateUrl: './yield-curve.template.html' })
export class YieldCurveComponent implements OnInit, AfterViewInit {
    @Input() startDate!: string;

    @Input() dateSeries: YieldCurveDateSeries[] = [];

    @Input() isDashboardModule: boolean = false;

    @Input() userCanEdit: boolean = false;

    @Input() chartCssClass: string = '';

    @Output() dateSeriesChange = new EventEmitter<YieldCurveDateSeries[]>();

    @ViewChild('featureNeededComponent') featureNeededComponent!: FeatureNeededComponent;

    chartElemId = `yieldCurveChart${uuid()}`;

    highchartOptions!: HighchartOptions;

    isLoading: boolean = false;

    exportOptions: SimpleDropdownConfig = YIELD_CURVE_EXPORT_OPTIONS;

    errorMessage: string = '';

    exportService!: ExportService;

    private chart: Highcharts.Chart | null = null;

    private showRecessions: boolean = false;

    private showRecessionsubscription!: Subscription;

    constructor(
        private targetRatesService: TargetRatesService,
        private featureService: FeatureService,
        public elementRef: ElementRef,
        private targetRatesAppStateService: TargetRatesAppStateService,
        private store: Store<{ reducers: TargetRatesRootStore }>
    ) {
        this.startDate = this.elementRef.nativeElement.getAttribute('startDate');
        this.dateSeries = this.elementRef.nativeElement.getAttribute('dateSeries');
        this.isDashboardModule = this.elementRef.nativeElement.getAttribute('isDashboardModule') === 'true';
        this.chartCssClass = this.elementRef.nativeElement.getAttribute('chartCssClass');
        this.userCanEdit = this.elementRef.nativeElement.getAttribute('userCanEdit') === 'true';
    }

    ngOnInit(): void {
        // initAppState should not be called in the dashboard module
        // This must be in ngOnInit since isDashboardModule isn't initialized yet in the constructor
        if (!this.isDashboardModule) {
            this.targetRatesAppStateService.initAppState();
            this.showRecessionsubscription = this.store
                .select('reducers')
                .pipe(select(getShowRecessions))
                .subscribe((showRecessiones) => {
                    this.showRecessions = showRecessiones;
                });
        }
        if (!this.dateSeries) {
            this.dateSeries = [];
            this.addDate();
        }
        this.highchartOptions = { ...HIGHCHARTS_YIELD_CURVE_DEFAULT_OPTIONS };
        this.getChartData();
    }

    ngAfterViewInit(): void {
        this.exportService = new ExportService(
            this.targetRatesService,
            this.featureService,
            this.featureNeededComponent,
            this.exportErrorHandler
        );
    }

    ngOnDestroy(): void {
        if (this.showRecessionsubscription) {
            this.showRecessionsubscription.unsubscribe();
        }
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
    }

    rerenderChart(): void {
        if (this.chart) {
            this.chart.destroy();
            this.chart = Highcharts.chart(this.chartElemId, this.highchartOptions);
        }
    }

    get activeDatesCount(): number {
        return this.dateSeries.length;
    }

    get canAddMoreDates(): boolean {
        return this.activeDatesCount < MAX_DATES;
    }

    dateChangeHandler(date: Date | null, index: number): void {
        if (!date || index >= this.activeDatesCount) {
            return;
        }
        this.dateSeries[index].date = date;
        this.dateSeriesChange.emit(this.dateSeries);
        this.getChartData();
    }

    addDate(): void {
        if (this.canAddMoreDates) {
            this.dateSeries.push({
                id: uuid(), // Generate unique ID for tracking
                date: new Date(this.startDate), // Use the given start date as a known valid initial Date value
            });
            this.dateSeriesChange.emit(this.dateSeries);
            this.getChartData();
        }
    }

    removeLastDate(): void {
        if (this.activeDatesCount > 1) {
            this.dateSeries.pop();
            this.dateSeriesChange.emit(this.dateSeries);
            this.getChartData();
        }
    }

    handleExport(option: string): void {
        switch (option) {
            case ExportOption.pdf:
                if (!this.featureService.userHasFeatures([STATIC_PDF_FEATURE])) {
                    this.featureNeededComponent.openModal(STATIC_PDF_FEATURE);
                } else {
                    this.exportPdf();
                }
                break;
            case ExportOption.download:
                this.exportService.exportHandler(ExportOption.download, this.getDates());
                break;
            case ExportOption.export:
                window.location.href = this.targetRatesService.getYieldCurveUrl(this.getDates());
                break;
            default:
                break;
        }
    }

    private exportPdf(): void {
        const dates = this.getDates();
        const fetchUrl = this.targetRatesService.getDownloadPdf(dates, this.showRecessions);
        fetchUrl.subscribe({
            next: (response: { report_url: string }) => {
                // Set `onbeforeunload` to null to avoid triggering the "unsaved changes" warning when downloading
                // the PDF. After the download finishes, set the value back to the function set by
                // `SavedItemDropdownComponent` .
                window.onbeforeunload = null;
                downloadUrlWrapper(response.report_url).subscribe();
            },
        });
    }

    private getDates(): string[] {
        return this.dateSeries.map((dateSerie) => dateSerie.date.toLocaleDateString('en-US'));
    }

    private exportErrorHandler = (_response: HttpErrorResponse): void => {
        this.errorMessage = 'Error exporting chart';
    };

    private getChartData(): void {
        this.isLoading = true;
        this.errorMessage = '';

        if (!this.dateSeries[0]) {
            this.errorMessage = 'Invalid Dates';
            return;
        }
        const dates = this.getDates();
        this.targetRatesService.getYieldCurveData(dates).subscribe({
            next: (response) => this.renderChart(response),
            error: () => {
                this.errorMessage = 'Unexpected error. Please re-try or contact support.';
                this.isLoading = false;
            },
        });
    }

    private renderChart(chartData: any): void {
        this.isLoading = false;
        const { series } = chartData;
        const { dates } = chartData.params;

        this.highchartOptions.series = series.map((serie: any, seriesIndex: number) => ({
            data: serie.data.map((value: string, pointIndex: number) => ({
                x: YIELD_CURVE_UNITS[pointIndex],
                y: value,
                custom: {
                    date: dates[seriesIndex],
                    term: YIELD_CURVE_TERMS[pointIndex], // Assign term to the point for easier tooltip display.
                },
            })),
        }));

        this.highchartOptions = Highcharts.merge(this.highchartOptions, {
            tooltip: {
                headerFormat: '',
                followPointer: true,
                formatter() {
                    const { y, point } = this;
                    const { date, term } = (point as YieldCurveChartPoint).custom;
                    return ` <div class="scatter-tooltip align-right user-select-none max-width-220">
                                <div class="highcharts-tooltip-title">${term} Treasury Yield</div>
                                <table class="highcharts-tooltip-table"><tbody>
                                    <tr><td class="text-right td-ellipsis-content">${y}%</td>
                                        <td class="td-ellipsis-content">${date}</td></tr>
                                </tbody></table>
                            </div>`;
                },
                useHTML: true,
                style: {
                    fontSize: 'inherit',
                },
            },
        } as HighchartOptions);

        const observer = new MutationObserver((_mutations) => {
            if (document.getElementById(this.chartElemId)) {
                observer.disconnect();
                this.chart = Highcharts.chart(this.chartElemId, this.highchartOptions);
            }
        });
        observer.observe(this.elementRef.nativeElement, { childList: true, subtree: true });
    }
}
